'use client';

import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { useBodyScrollLock } from '@paeffgen/react/crm-documents/util';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Additional classes, e.g. to adjust max-width */
  className?: string;
  /** Optional content rendered directly below the title inside the header */
  subtitle?: React.ReactNode;
  /**
   * Optional node rendered in the header, horizontally centered between the
   * title and the close button (e.g. a primary action). When omitted the
   * header keeps its default title-left / close-right layout.
   */
  headerActions?: React.ReactNode;
  /** Overrides the default body padding ('p-6') — use e.g. 'p-3' for compact menus */
  bodyClassName?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  subtitle,
  headerActions,
  bodyClassName,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // onClose wird von Aufrufern meist als Inline-Arrow übergeben und wechselt
  // damit bei jedem Parent-Render die Identität. Über die Ref gehalten, damit
  // der Focus-Effect NICHT neu läuft (sonst springt der Fokus bei jedem
  // Tastendruck in einem Modal-Suchfeld zurück auf den Schließen-Button).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? dialog)?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('keydown', handleKey);
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Dialog schließen"
      className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors shrink-0"
    >
      <X size={20} />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 transition-opacity"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(
          'bg-white rounded-xl shadow-2xl dark:shadow-none dark:ring-1 dark:ring-white/10 relative z-10 w-full max-h-[90dvh] flex flex-col animate-in fade-in zoom-in-95 duration-200 focus:outline-none',
          className || 'max-w-2xl',
        )}
      >
        <div
          className={clsx(
            'flex gap-3 p-6 border-b border-gray-100',
            headerActions ? 'items-center' : 'items-start justify-between',
          )}
        >
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-bold text-gray-900 leading-snug">
              {title}
            </h2>
            {subtitle && <div className="mt-0.5">{subtitle}</div>}
          </div>
          {headerActions ? (
            <>
              {/* Zentriert zwischen Titel (flex-1) und Close (flex-1) */}
              <div className="shrink-0">{headerActions}</div>
              <div className="flex-1 flex justify-end">{closeButton}</div>
            </>
          ) : (
            closeButton
          )}
        </div>

        <div className={clsx('overflow-y-auto', bodyClassName ?? 'p-6')}>
          {children}
        </div>
      </div>
    </div>
  );
}
